/** tm/legal/useLegalView.ts — 화면 상태(주제·탭·법령 종류·세부·검색·선택) + 사용자별로 마지막 주제·탭 기억 (localStorage, 실패해도 무시) */
import { useCallback, useEffect, useState } from 'react';
import type { FeedTab, LawKind, LegalNavParams, Selection, Topic } from './legalTypes';
import type { FilterValue } from './LegalFilterColumn';

const TABS: FeedTab[] = ['all', 'law', 'prec', 'expc', 'bill', 'notice', 'cabinet', 'news'];
interface Saved { topic?: string; tab?: FeedTab }
const key = (uid: string) => `tm_legal_view:${uid || 'me'}`;
function load(uid: string): Saved {
  try {
    const v = JSON.parse(localStorage.getItem(key(uid)) || '{}') as Saved;
    return { topic: typeof v.topic === 'string' ? v.topic : undefined, tab: v.tab && TABS.includes(v.tab) ? v.tab : undefined };
  } catch { return {}; }
}
function save(uid: string, v: Saved) {
  try { localStorage.setItem(key(uid), JSON.stringify({ ...load(uid), ...v })); } catch { /* 저장 불가 환경 */ }
}
const selOf = (p: LegalNavParams | undefined): Selection => (p?.itemId ? { kind: p.kind || 'legal', id: p.itemId } : null);

export function useLegalView(userId: string, navParams: LegalNavParams | undefined, topics: Topic[]) {
  const [topic, setTopicRaw] = useState<string | null>(navParams?.topic ?? null);
  const [starred, setStarred] = useState(false);
  const [tab, setTabRaw] = useState<FeedTab>(() => (navParams?.itemId || navParams?.alerts ? 'all' : load(userId).tab || 'all'));
  const [lawKind, setLawKind] = useState<LawKind | ''>('');
  const [sub, setSub] = useState('');
  const [q, setQ] = useState('');
  const [onlyNew, setOnlyNew] = useState(!!navParams?.alerts);
  const [sel, setSel] = useState<Selection>(() => selOf(navParams));

  // 첫 주제: 이동 파라미터 → 지난번 본 주제 → 전체 주제(개요). 없는 주제(예: 법령 개정 묶음)면 전체
  useEffect(() => {
    if (!topics.length) return;
    if (topic && !topics.some(t => t.label === topic)) { setTopicRaw(''); return; }
    if (topic !== null) return;
    const last = load(userId).topic;
    setTopicRaw(last !== undefined && (last === '' || topics.some(t => t.label === last)) ? last : '');
  }, [topics, topic, userId]);
  useEffect(() => {
    if (navParams?.topic !== undefined) { setTopicRaw(navParams.topic); setStarred(false); setOnlyNew(!!navParams.alerts); }
    if (navParams?.itemId) { setSel(selOf(navParams)); setTabRaw('all'); setSub(''); setLawKind(''); }
  }, [navParams]);

  const selectTopic = useCallback((label: string, star = false) => {
    setTopicRaw(label); setStarred(star); setSub(''); setSel(null); setOnlyNew(false);
    if (!star) save(userId, { topic: label });
  }, [userId]);
  /** 「새 소식」 — 모든 주제의 새 글만 */
  const selectNew = useCallback(() => { setTopicRaw(''); setStarred(false); setSub(''); setSel(null); setOnlyNew(true); }, []);
  const setTab = useCallback((t: FeedTab) => { setTabRaw(t); setSel(null); if (t !== 'law') setLawKind(''); save(userId, { tab: t }); }, [userId]);
  /** 알림에서 열 때: 해당 주제·전체 탭으로 */
  const jumpTo = useCallback((label: string | null, s: Selection) => {
    if (label && topics.some(t => t.label === label)) { setTopicRaw(label); setStarred(false); save(userId, { topic: label }); }
    setTabRaw('all'); setSub(''); setLawKind(''); setOnlyNew(false); setSel(s);
  }, [topics, userId]);

  /** 필터 칼럼·시트에서 한 번에 바꾸기 (주제가 바뀌면 세부 주제를 풀고, 무엇이든 바뀌면 선택을 닫는다) */
  const applyFilter = useCallback((f: FilterValue) => {
    setTopicRaw(f.topic); setStarred(f.starred); setOnlyNew(f.onlyNew); setTabRaw(f.tab); setLawKind(f.tab === 'law' ? f.lawKind : '');
    setSub(cur => (f.topic === topic ? cur : '')); setSel(null);
    save(userId, { ...(f.starred || f.onlyNew ? {} : { topic: f.topic }), tab: f.tab });
  }, [userId, topic]);

  return { topic, starred, tab, lawKind, sub, q, onlyNew, sel, applyFilter, setSub, setQ, setOnlyNew, setSel, setTab, setLawKind, selectTopic, selectNew, jumpTo };
}
